import type { ReactNode } from "react";
import { useTranslate } from "ra-core";

import { findDealLabel } from "../deals/dealUtils";
import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  LiderExpiringLead,
  LiderFunnelStage,
  LiderPosiIncompleteLead,
} from "./liderLeadSignals";

/**
 * Almikia Epic E7 (TASK-093): the Líder BPO dashboard's lead-signal blocks --
 * the team funnel ("where is each lead"), "Por vencer" (the Prospecto 24h
 * SLA, DOMAIN RULE (k): a commercial signal, never a per-advisor count) and
 * "POSI incompleto", plus the "Operaciones con documentos pendientes" slot as
 * a designed empty state (data arrives with Epic E4/E5). Pure presentation --
 * `LiderDashboard.tsx` owns the data fetching, `liderLeadSignals.ts` the
 * grouping/selection logic.
 */

type Translate = ReturnType<typeof useTranslate>;

export interface LiderLeadSignalsProps {
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  funnel: LiderFunnelStage[];
  expiringLeads: LiderExpiringLead[];
  posiIncompleteLeads: LiderPosiIncompleteLead[];
}

const SectionShell = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <h2 className="text-xl font-semibold text-muted-foreground">{title}</h2>
    {children}
  </div>
);

const FunnelSection = ({
  funnel,
  translate,
}: {
  funnel: LiderFunnelStage[];
  translate: Translate;
}) => {
  const { dealStages } = useConfigurationContext();
  return (
    <SectionShell title={translate("crm.dashboard.lider.funnel_title")}>
      {funnel.length === 0 ? (
        <ListEmptyState
          title={translate("crm.dashboard.lider.funnel_empty_title")}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {funnel.map((group) => (
            <div key={group.stage} className="flex flex-col gap-2">
              <p className="text-base font-bold">
                {findDealLabel(dealStages, group.stage)}
              </p>
              <div className="flex flex-col gap-2">
                {group.leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-card p-3"
                  >
                    <span className="truncate text-base">{lead.name}</span>
                    <span className="shrink-0 text-base text-muted-foreground">
                      {lead.advisorName}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};

const ExpiringSection = ({
  leads,
  translate,
}: {
  leads: LiderExpiringLead[];
  translate: Translate;
}) => (
  <SectionShell title={translate("crm.dashboard.lider.expiring_title")}>
    {leads.length === 0 ? (
      <ListEmptyState
        title={translate("crm.dashboard.lider.expiring_empty_title")}
      />
    ) : (
      <div className="flex flex-col gap-2">
        {leads.map((lead) => (
          <div
            key={lead.id}
            className="flex items-center justify-between gap-3 rounded-2xl bg-card p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-bold">{lead.name}</p>
              <p className="truncate text-base text-muted-foreground">
                {lead.advisorName}
              </p>
            </div>
            <StatusPill
              tone="not-ok"
              text={translate("crm.dashboard.lider.expiring_hours", {
                hours: lead.hours,
              })}
            />
          </div>
        ))}
      </div>
    )}
  </SectionShell>
);

const PosiIncompleteSection = ({
  leads,
  translate,
}: {
  leads: LiderPosiIncompleteLead[];
  translate: Translate;
}) => (
  <SectionShell title={translate("crm.dashboard.lider.posi_incomplete_title")}>
    {leads.length === 0 ? (
      <ListEmptyState
        title={translate("crm.dashboard.lider.posi_incomplete_empty_title")}
      />
    ) : (
      <div className="flex flex-col gap-2">
        {leads.map((lead) => (
          <div key={lead.id} className="rounded-2xl bg-card p-3">
            <p className="truncate text-base font-bold">{lead.name}</p>
            <p className="truncate text-base text-muted-foreground">
              {lead.advisorName} ·{" "}
              {translate("crm.dashboard.hoy.reason.posi_incomplete", {
                answered: lead.answered,
                // Every missing field, comma-joined (acceptance criteria:
                // "the missing answers named", plural) -- the shared key is
                // also used by `AdvisorToday.tsx` for a single field, so the
                // join happens here rather than changing the key's shape.
                field: lead.missingFields
                  .map((field) =>
                    translate(`crm.dashboard.hoy.posi_field.${field}`),
                  )
                  .join(", "),
              })}
            </p>
          </div>
        ))}
      </div>
    )}
  </SectionShell>
);

const PendingOperationsSection = ({ translate }: { translate: Translate }) => (
  <SectionShell title={translate("crm.dashboard.lider.pending_ops_title")}>
    <ListEmptyState
      title={translate("crm.dashboard.lider.pending_ops_empty_title")}
      description={translate(
        "crm.dashboard.lider.pending_ops_empty_description",
      )}
    />
  </SectionShell>
);

export const LiderLeadSignals = ({
  isPending,
  error,
  onRetry,
  funnel,
  expiringLeads,
  posiIncompleteLeads,
}: LiderLeadSignalsProps) => {
  const translate = useTranslate();

  if (error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={onRetry}
      />
    );
  }
  if (isPending) {
    return <ListRowsSkeleton rows={6} columns={2} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <FunnelSection funnel={funnel} translate={translate} />
      <ExpiringSection leads={expiringLeads} translate={translate} />
      <PosiIncompleteSection
        leads={posiIncompleteLeads}
        translate={translate}
      />
      <PendingOperationsSection translate={translate} />
    </div>
  );
};
